import { LINE_ITEM_TYPES, COLUMN_CONFIG } from "./constants";
import { getUserFullName } from "../../../../../utils/business";

/**
 * Creates item data from a selected option
 * Converts an API option object into a complete line item with all necessary fields
 *
 * @param {Object} selectedOption - The selected option from the API
 * @param {Object} baseItem - The base item to merge data into
 * @returns {Object} Complete line item object ready for onChange or onDuplicate
 */
export const createItemDataFromOption = (selectedOption, baseItem) => {
  const typeConfig = LINE_ITEM_TYPES[baseItem.type] || LINE_ITEM_TYPES.labor;
  const cols = COLUMN_CONFIG[baseItem.type] || COLUMN_CONFIG.labor;

  // Determine the display name and rates based on type
  let displayName = "";
  let rateRT = null;
  let rateOT = null;
  let unitOverride = null;

  if (baseItem.type === "labor") {
    // Workers have 'first' and 'last' properties, not 'name'
    displayName =
      getUserFullName(selectedOption) ||
      selectedOption.name ||
      `Worker ${selectedOption.id}`;
    rateRT = selectedOption.worker_cost || null;
    // OT rate typically 1.5x RT for labor, but comes from API
    rateOT =
      selectedOption.worker_cost_ot ||
      (rateRT ? Math.round(rateRT * 1.5 * 100) / 100 : null);
  } else if (baseItem.type === "material") {
    displayName = selectedOption.name;
    rateRT = selectedOption.cost_per_unit || null;
    // Material uses price_per_unit_rt from Asset
    return {
      ...baseItem,
      option_id: selectedOption.id,
      option_name: displayName,
      unit: selectedOption.unit_name || typeConfig.defaultUnit,
      rate_rt: rateRT,
      rate_ot: null, // Materials don't have OT pricing
      quantity: baseItem.quantity || 1,
    };
  } else if (baseItem.type === "equipment") {
    displayName = selectedOption.name;
    rateRT = selectedOption.own_cost || null; // price_per_unit_rt
    rateOT = selectedOption.rent_cost || null; // price_per_unit_ot
  } else if (baseItem.type === "concrete") {
    displayName = selectedOption.design_number;
    rateRT = selectedOption.cost_per_yard || null;
  } else if (
    baseItem.type === "subcontractors" ||
    baseItem.type === "dumping"
  ) {
    displayName = selectedOption.name;
    rateRT = selectedOption.cost_per_hour || null;
    unitOverride = typeConfig.defaultUnit;
  }

  return {
    ...baseItem,
    option_id: selectedOption.id,
    option_name: displayName,
    unit: unitOverride || baseItem.unit,
    rate_rt: rateRT,
    rate_ot: rateOT,
    quantity: cols.quantity ? baseItem.quantity || 1 : 0,
    rt_hours: cols.rtHours ? baseItem.rt_hours || 0 : 0,
    ot_hours: cols.otHours ? baseItem.ot_hours || 0 : 0,
  };
};

/**
 * Refill catalog-backed line items from current reference data.
 *
 * Used once when a T&M price is first opened from a report, before a
 * TMPrice row exists. Each item is matched by `option_id` in the same
 * catalog the price form uses when a user picks that option, then passed
 * through {@link createItemDataFromOption}. Report quantities and hours
 * stay on the item. Custom rows, rows with no id, and rows whose id is
 * missing from the catalog are left unchanged.
 *
 * @param {Array<Object>} lineItems Report line items to prefill.
 * @returns {Promise<Array<Object>>} Line items with fresh auto-filled fields.
 */
export async function refreshLineItemsFromCatalog(lineItems) {
  if (!Array.isArray(lineItems) || lineItems.length === 0) {
    return lineItems || [];
  }

  const typesNeeded = [
    ...new Set(
      lineItems
        .map((item) => item?.type)
        .filter((type) => LINE_ITEM_TYPES[type]?.fetchOptions)
    ),
  ];

  // One catalog request per type present on the report, not one per row.
  const catalogs = {};
  await Promise.all(
    typesNeeded.map(async (type) => {
      try {
        const data = await LINE_ITEM_TYPES[type].fetchOptions();
        catalogs[type] = Array.isArray(data) ? data : [];
      } catch (error) {
        // Keep the report snapshot for this type if the catalog cannot be loaded.
        console.error(`Failed to refresh ${type} catalog for T&M pricing:`, error);
        catalogs[type] = null;
      }
    })
  );

  return lineItems.map((item) => {
    const catalog = catalogs[item?.type];
    if (!catalog || item?.option_id == null || item.option_id === "") {
      return item;
    }

    const match = catalog.find(
      (option) => option && String(option.id) === String(item.option_id)
    );
    if (!match) {
      return item;
    }

    return createItemDataFromOption(match, item);
  });
}

/**
 * Calculates the total cost for a line item
 * Uses different calculation methods based on item type
 *
 * @param {Object} item - The line item to calculate total for
 * @returns {number} The total cost
 */
export const calculateLineItemTotal = (item) => {
  const qty = parseFloat(item.quantity) || 0;
  const rtHrs = parseFloat(item.rt_hours) || 0;
  const otHrs = parseFloat(item.ot_hours) || 0;
  const rateRT = parseFloat(item.rate_rt) || 0;
  const rateOT = parseFloat(item.rate_ot) || 0;

  if (item.type === "labor" || item.type === "custom_labor") {
    // Labor: RT Hours * RT Rate + OT Hours * OT Rate
    return rtHrs * rateRT + otHrs * rateOT;
  } else if (item.type === "equipment" || item.type === "custom_equipment") {
    // Equipment: Quantity * (RT Hours * RT Rate + OT Hours * OT Rate)
    return qty * (rtHrs * rateRT + otHrs * rateOT);
  } else {
    // Material, Concrete, Subcontractor, Dumping: Quantity * RT Rate
    return qty * rateRT;
  }
};

/**
 * Sum line-item totals and round once to cents.
 *
 * @param {Array<Object>} items Line items.
 * @returns {number} Total cost.
 */
export function calculateTotalCost(items) {
  const total = (items || []).reduce(
    (sum, item) => sum + calculateLineItemTotal(item),
    0
  );
  return Math.round(total * 100) / 100;
}

/**
 * Formats API options into SearchableDetailsDropdown format
 * Handles type-specific label formatting and tooltips
 *
 * @param {Array} options - Array of options from the API
 * @param {string} itemType - The type of item (labor, equipment, etc.)
 * @returns {Array} Formatted options for SearchableDetailsDropdown
 */
export const formatSearchableDetailsDropdownOptions = (options, itemType) => {
  return options
    .filter((option) => option != null) // Filter out null/undefined options
    .map((option) => {
      let label = "";
      let tooltip = null;

      if (itemType === "labor") {
        // Workers have 'first' and 'last' properties, not 'name'
        label = getUserFullName(option) || option.name || `Worker ${option.id}`;
      } else if (itemType === "concrete") {
        label = option.design_number || "";
      } else if (itemType === "equipment" || itemType === "material") {
        label = `${option.internal_id ? option.internal_id + " - " : ""}${option.name || ""}`;
        tooltip = option.notes || null;
      } else {
        label = option.name || "";
      }

      return {
        id: option.id,
        label: label || `Option ${option.id}`, // Ensure label is never empty
        tooltip: tooltip,
      };
    });
};
